import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PlacePicker from './PlacePicker';
import useFormPersist from '../hooks/useFormPersist';
import useLocale from '../hooks/useLocale';

const Form = styled.form``;
const Grid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:${p => p.theme.spacing[4]}px;
  @media(max-width:${p => p.theme.breakpoints.desktop}px){grid-template-columns:1fr;}
`;
const Field = styled.div`display:flex;flex-direction:column;gap:6px;`;
const Label = styled.label`
  font-size:${p => p.theme.typography.sizes.sm};
  font-weight:${p => p.theme.typography.weights.semibold};
  color:${p => p.theme.colors.text.primary};
`;
const Input = styled.input`
  padding:10px 12px;
  border:1px solid ${p => p.theme.colors.border};
  border-radius:${p => p.theme.radius.md}px;
  font-size:${p => p.theme.typography.sizes.md};
  &:focus{border-color:${p => p.theme.colors.primary};outline:none;box-shadow:0 0 0 2px rgba(108,99,255,0.15);}
`;
const SubmitButton = styled.button`
  width:100%;padding:12px;margin-top:${p => p.theme.spacing[4]}px;
  border:none;border-radius:${p => p.theme.radius.md}px;
  background:${p => p.theme.colors.primary};color:${p => p.theme.colors.surface};
  font-size:${p => p.theme.typography.sizes.md};
  font-weight:${p => p.theme.typography.weights.semibold};
  cursor:pointer;
  &:hover{background:#5a52d5;}
  &:disabled{opacity:0.6;cursor:not-allowed;}
`;
const ErrorBox = styled.div`
  background:${p => p.theme.colors.error.bg};
  color:${p => p.theme.colors.error.text};
  border-radius:${p => p.theme.radius.md}px;
  padding:${p => p.theme.spacing[3]}px ${p => p.theme.spacing[4]}px;
  margin-top:${p => p.theme.spacing[3]}px;
`;

export default function LocationForm({
  placeValue, lat, lon, timezone,
  onPlaceChange, date, onDateChange,
  showDate = false, onSubmit,
  submitLabel, loading = false, error = null,
  pageName, lang = 'en',
}) {
  const t = useLocale(lang, 'vedic');
  const ft = t.forms || {};
  const values = { place: placeValue, lat, lon, timezone, ...(showDate ? { date } : {}) };
  const onChange = useCallback(() => {}, []);

  useFormPersist(pageName, values, onChange);

  useEffect(() => {
    if (placeValue) return;
    try {
      const lastPlace = sessionStorage.getItem('vedic_last_place');
      if (lastPlace) {
        const parsed = JSON.parse(lastPlace);
        if (parsed && parsed.place && onPlaceChange) {
          onPlaceChange(parsed);
        }
      }
    } catch { /* ignore parse errors */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid>
        <PlacePicker
          value={placeValue}
          lat={lat}
          lon={lon}
          timezone={timezone}
          onChange={onPlaceChange}
          label={ft.locationLabel || ft.place || 'Location'}
          disabled={loading}
        />
        {showDate && (
          <Field>
            <Label>{ft.date || 'Date'}</Label>
            <Input type="date" value={date || ''} onChange={e => onDateChange(e.target.value)} disabled={loading} />
          </Field>
        )}
      </Grid>
      <SubmitButton type="submit" disabled={loading}>
        {loading ? (ft.loading || 'Computing...') : (submitLabel || ft.compute || 'Compute')}
      </SubmitButton>
      {error && <ErrorBox>{error}</ErrorBox>}
    </Form>
  );
}
